import { useCallback, CSSProperties, ReactNode } from 'react';
import { List, RowComponentProps } from 'react-window';
import AutoSizer from './AutoSizer';

interface VirtualListProps<T> {
  items: T[];
  itemHeight: number;
  renderItem: (item: T, index: number, style: CSSProperties) => ReactNode;
  className?: string;
  overscanCount?: number;
  maxHeight?: number;
}

/**
 * VirtualList - A virtualized list component that only renders visible items
 *
 * This significantly improves scroll performance for long lists by:
 * 1. Only rendering items that are currently visible in the viewport
 * 2. Recycling DOM nodes as the user scrolls
 * 3. Reducing memory usage for large datasets
 *
 * @example
 * <VirtualList
 *   items={incidents}
 *   itemHeight={180}
 *   renderItem={(incident, index, style) => (
 *     <div style={style} key={incident.id}>
 *       <IncidentCard incident={incident} />
 *     </div>
 *   )}
 * />
 */
export function VirtualList<T>({
  items,
  itemHeight,
  renderItem,
  className = '',
  overscanCount = 5,
  maxHeight = 600,
}: VirtualListProps<T>) {
  const Row = useCallback(
    ({ index, style }: RowComponentProps) => {
      const item = items[index];
      return <>{renderItem(item, index, style)}</>;
    },
    [items, renderItem]
  );

  if (items.length === 0) {
    return null;
  }

  // Calculate the actual height needed
  const totalHeight = items.length * itemHeight;
  const listHeight = Math.min(totalHeight, maxHeight);

  return (
    <div className={className} style={{ height: listHeight, width: '100%' }}>
      <List
        rowCount={items.length}
        rowHeight={itemHeight}
        rowComponent={Row}
        rowProps={{}}
        overscanCount={overscanCount}
        style={{ width: '100%', height: listHeight, willChange: 'transform' }}
      />
    </div>
  );
}

/**
 * VirtualListAuto - A virtualized list that automatically sizes to container
 *
 * Use this when you want the list to fill its parent container
 */
export function VirtualListAuto<T>({
  items,
  itemHeight,
  renderItem,
  className = '',
  overscanCount = 5,
}: Omit<VirtualListProps<T>, 'maxHeight'>) {
  const Row = useCallback(
    ({ index, style }: RowComponentProps) => {
      const item = items[index];
      return <>{renderItem(item, index, style)}</>;
    },
    [items, renderItem]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={className} style={{ height: '100%', minHeight: 400 }}>
      <AutoSizer>
        {({ height, width }) => (
          <List
            rowCount={items.length}
            rowHeight={itemHeight}
            rowComponent={Row}
            rowProps={{}}
            overscanCount={overscanCount}
            style={{ width, height, willChange: 'transform' }}
          />
        )}
      </AutoSizer>
    </div>
  );
}

export default VirtualList;
